import React, { useState } from 'react';
import { toggleWebhook, testFireWebhook } from '../../services/liveStamping';
import { toast } from 'react-toastify';

const EVENT_TYPES = [
  'order.placed',
  'order.executing',
  'order.metadata_ready',
  'order.completed',
  'order.rejected',
];

const isProd = process.env.REACT_APP_ENV === 'production';

const AdminWebhookConfig = ({ tenantId }) => {
  const [isActive, setIsActive] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [testEventType, setTestEventType] = useState(EVENT_TYPES[0]);
  const [firing, setFiring] = useState(false);
  const [error, setError] = useState('');

  if (!tenantId) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-gray-400 text-center">
        Enter a Tenant ID above to manage their webhook settings.
      </div>
    );
  }

  const handleToggle = async () => {
    setToggling(true);
    setError('');
    try {
      await toggleWebhook(tenantId, !isActive);
      setIsActive(v => !v);
      toast.success(`Webhook delivery ${!isActive ? 'enabled' : 'disabled'} for ${tenantId}`);
    } catch (err) {
      setError(err.message || 'Toggle failed.');
    } finally {
      setToggling(false);
    }
  };

  const handleTestFire = async () => {
    setFiring(true);
    setError('');
    try {
      await testFireWebhook(tenantId, testEventType);
      toast.success(`Test event queued for delivery (${testEventType})`);
    } catch (err) {
      setError(err.message || 'Test fire failed.');
    } finally {
      setFiring(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100">
      {/* Tenant ID display */}
      <div className="px-5 py-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Tenant</p>
        <p className="font-mono text-sm text-gray-800">{tenantId}</p>
      </div>

      {/* Enable / Disable toggle */}
      <div className="px-5 py-4 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-gray-800">Webhook Delivery</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {isActive ? 'Currently enabled — events are being delivered.' : 'Currently disabled — no events will be delivered.'}
          </p>
        </div>
        <button
          onClick={handleToggle}
          disabled={toggling}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-50 ${
            isActive ? 'bg-indigo-600' : 'bg-gray-300'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Test fire */}
      <div className="px-5 py-4 space-y-3">
        <p className="text-sm font-medium text-gray-800">Send Test Event</p>
        {isProd ? (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Test fire is unavailable in production.
          </p>
        ) : (
          <div className="flex items-center gap-2">
            <select
              value={testEventType}
              onChange={e => setTestEventType(e.target.value)}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              {EVENT_TYPES.map(et => (
                <option key={et} value={et}>{et}</option>
              ))}
            </select>
            <button
              onClick={handleTestFire}
              disabled={firing}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
            >
              {firing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
              {firing ? 'Firing…' : 'Fire Test'}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="px-5 py-3">
          <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        </div>
      )}
    </div>
  );
};

export default AdminWebhookConfig;
